import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { getMarketplaceFulfillmentIcon, getMarketplaceFulfillmentLabel, isAmazonMarketplace } from "@/utils/marketplace";

export interface OfferCardItem {
  slug: string;
  image: string;
  name: string;
  price: number;
  originalPrice: number;
  discount: number;
  marketplace: string;
  freeShipping: boolean;
  fulfillment?: boolean;
  officialStoreName?: string;
  affiliateLink: string;
  isFlash?: boolean;
  category?: string;
  categorySlug?: string;
  subcategory?: string;
  subcategorySlug?: string;
}

interface OfferCardProps {
  item: OfferCardItem;
  className?: string;
  extra?: ReactNode;
}

const marketplaceColors: Record<string, string> = {
  Amazon: "bg-amber-100 text-amber-700",
  "Mercado Livre": "bg-yellow-100 text-yellow-700",
  Shopee: "bg-orange-100 text-orange-700",
  "Magazine Luiza": "bg-blue-100 text-blue-700",
  Americanas: "bg-red-100 text-red-700",
};

export default function OfferCard({ item, className = "w-[240px] md:w-[264px] flex-shrink-0", extra }: OfferCardProps) {
  const fulfillmentLabel = getMarketplaceFulfillmentLabel(item.marketplace);
  const fulfillmentIcon = getMarketplaceFulfillmentIcon(item.marketplace);
  const fulfillmentColors = isAmazonMarketplace(item.marketplace)
    ? "text-amber-700 bg-amber-50"
    : "text-sky-600 bg-sky-50";

  const taxonomyLabel = item.subcategory?.trim() && item.subcategory !== item.category
    ? item.subcategory
    : item.category;
  const taxonomyPath = item.subcategorySlug?.trim() && item.categorySlug?.trim() && item.subcategory !== item.category
    ? `/categoria/${item.categorySlug}/${item.subcategorySlug}`
    : item.categorySlug?.trim()
      ? `/categoria/${item.categorySlug}`
      : null;

  return (
    <div className={`group bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-all duration-200 flex flex-col ${className}`}>
      <Link to={`/produto/${item.slug}`} className="block relative overflow-hidden bg-gray-50">
        <div className="w-full h-[211px] p-3 flex items-center justify-center">
          <img
            src={item.image}
            alt={item.name}
            className="max-w-full max-h-full w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        {item.discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-black px-2 py-0.5 rounded-md">
            -{item.discount}%
          </div>
        )}
        {item.isFlash && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-orange-500 text-white text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-md">
            <i className="ri-flashlight-line text-xs"></i>
            Relâmpago
          </div>
        )}
      </Link>

      <div className="p-3 flex flex-col flex-1">
        {taxonomyLabel && taxonomyPath && (
          <div className="mb-2">
            <Link
              to={taxonomyPath}
              className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-600 hover:bg-orange-100"
            >
              <i className="ri-folder-2-line text-xs"></i>
              {taxonomyLabel}
            </Link>
          </div>
        )}

        <Link to={`/produto/${item.slug}`}>
          <p className="text-gray-800 text-[13px] font-semibold leading-snug line-clamp-2 mb-2 group-hover:text-orange-600 transition-colors">
            {item.name}
          </p>
        </Link>

        <div className="mb-3">
          {item.originalPrice > item.price && (
            <p className="text-[11px] text-gray-400 line-through">
              R$ {item.originalPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </p>
          )}
          <p className="text-lg font-bold text-gray-900">
            R$ {item.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
          </p>
        </div>

        {item.officialStoreName && (
          <div className="mb-2 inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
            <i className="ri-verified-badge-fill text-xs"></i>
            <span className="truncate">{item.officialStoreName}</span>
          </div>
        )}

        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${marketplaceColors[item.marketplace] || "bg-gray-100 text-gray-600"}`}>
            {item.marketplace}
          </span>
          {item.fulfillment && (
            <span className={`text-[11px] font-semibold uppercase tracking-wide flex items-center gap-0.5 px-2 py-0.5 rounded-full ${fulfillmentColors}`}>
              <i className={`${fulfillmentIcon} text-xs`}></i>
              {fulfillmentLabel}
            </span>
          )}
        </div>

        {item.freeShipping && (
          <div className="mb-2">
            <span className="text-green-600 text-[11px] font-semibold uppercase tracking-wide flex items-center gap-0.5 bg-green-50 px-2 py-0.5 rounded-full w-fit">
              <i className="ri-truck-line text-xs"></i>
              Frete Grátis
            </span>
          </div>
        )}

        {!item.freeShipping && <div className="mb-2 h-[22px]" />}

        {extra && <div className="mb-3">{extra}</div>}

        <div className="mt-auto">
          <a
            href={item.affiliateLink}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={(e) => e.stopPropagation()}
            className="block w-full py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold text-center transition-colors cursor-pointer whitespace-nowrap"
          >
            Ver Oferta
            <i className="ri-external-link-line ml-1"></i>
          </a>
        </div>
      </div>
    </div>
  );
}
